import { DSBottomSheet } from '@repo/design-system/index'
import { QIcon_certified, QIcon_tutor_default } from '@repo/icons/index'
import classNames from 'classnames'
import { useState } from 'react'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { s_review, s_schedule, s_upcoming_lessons } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { AssignedTutor } from '../../../modules/lessonTypes'
import urls from '../../../modules/urls'
import { isB2BHRGroup } from '../../../modules/utils'
import TutorBody, { TUTOR_PROFILE_TAB } from '../../student/new-portal/tutors/TutorBody'

interface TutorAvatarPropsInterface {
  tutor: AssignedTutor
  isRookiePointBack?: boolean
  isNoshow?: boolean
  matching_after?: number
  isTutorMatching?: boolean
}

const TutorAvatarSkeleton = () => {
  return (
    <div className="gap-[8px] flex flex-row items-start justify-start">
      <div className="h-[48px] w-[48px] animate-pulse rounded-full bg-gray-100"></div>
      <div className="h-[48px] flex flex-col items-start justify-between">
        <div className="h-[20px] w-[100px] animate-pulse rounded-md bg-gray-100"></div>
        <div className="h-[20px] w-[150px] animate-pulse rounded-md bg-gray-100"></div>
      </div>
    </div>
  )
}
// 튜터 정보 들어가는 컴포넌트
// CONFIRM_STATUS
const TutorAvatar = ({
  tutor,
  isNoshow = false,
  isRookiePointBack,
  matching_after,
  isTutorMatching = false,
}: TutorAvatarPropsInterface) => {
  const { t } = useTranslation()
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const { currentUser } = useCurrentUser()
  const isB2BUser = isB2BHRGroup(currentUser)

  const onClickTutorProfile = (e) => {
    if (isTutorMatching) {
      return
    }
    router.push(urls.student.newPortal.tutors.detail.as(tutor.id), {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(urls.webview.tutors.detail.as(tutor.id, TUTOR_PROFILE_TAB.SUMMARY)),
      },
    })
  }
  if (!tutor) {
    return <TutorAvatarSkeleton />
  }
  if (isTutorMatching) {
    return (
      <>
        <div className="flex w-full cursor-pointer flex-row items-center justify-start">
          <div className="relative">
            <QIcon_tutor_default width={48} height={48} />
            <div className="absolute top-[30px] right-[-1px]">
              <QIcon_certified className="text-purple-500" />
            </div>
          </div>
          <div className="ml-[8px] flex h-[48px] flex-col items-start justify-start overflow-x-hidden">
            {isRookiePointBack && !isB2BUser && <RookieTutorBadge />}
            <div className={classNames('Body2-14Bd md:Body1-16Bd', 'text-gray-500')}>
              {t(s_schedule.automatic_matching_for_tutor)}
            </div>
            <div className="w-full overflow-hidden">{t(s_upcoming_lessons.compatible_tutor)}</div>
          </div>
          {showBottomSheet && (
            <DSBottomSheet
              id={`bottom-sheet-tutor-${tutor.id}`}
              isOpen={showBottomSheet}
              setIsOpen={setShowBottomSheet}
            >
              <div className="scrollbar-new">
                <TutorBody id={tutor.id} noReserve setShowBottomSheet={setShowBottomSheet} />
              </div>
            </DSBottomSheet>
          )}
        </div>
      </>
    )
  } else {
    return (
      <>
        <div
          className="flex w-full cursor-pointer flex-row items-center justify-start"
          onClick={tutor.id && onClickTutorProfile}
        >
          {tutor.image_url ? (
            <div className="h-[48px] min-w-[48px] cursor-pointer">
              <img
                onClick={() => null}
                src={tutor.image_url}
                className="h-[48px] w-[48px] rounded-full object-cover"
                alt={`tutorImage_${tutor.tutor_name}`}
              />
            </div>
          ) : (
            <>
              {isTutorMatching ? (
                <div className="relative">
                  <QIcon_tutor_default width={48} height={48} />

                  <div className="absolute top-[30px] right-[-1px]">
                    <QIcon_certified className="text-purple-500" />
                  </div>
                </div>
              ) : (
                <QIcon_tutor_default width={48} height={48} />
              )}
            </>
          )}
          <div className="ml-[8px] flex flex-col items-start justify-start overflow-x-hidden">
            {isRookiePointBack && !isB2BUser && <RookieTutorBadge />}
            {!isNoshow ? (
              <div
                className={classNames(
                  'Body2-14Bd md:Body1-16Bd',
                  isNoshow ? 'text-gray-300' : isTutorMatching ? 'text-gray-500' : 'text-black'
                )}
              >
                {tutor.tutor_name ? tutor.tutor_name : isTutorMatching && t(s_schedule.automatic_matching_for_tutor)}
              </div>
            ) : (
              <div className="Body2-14Bd md:Body1-16Bd text-gray-300">{tutor.tutor_name}</div>
            )}
            <div className="w-full overflow-hidden">
              {!isNoshow ? (
                <p className="line-clamp-1 keep-all overflow-hidden Caption-12Md md:Body2-14Md text-gray-700">
                  {tutor.major ? tutor.major : isTutorMatching && t(s_upcoming_lessons.compatible_tutor)}
                </p>
              ) : (
                <p className="line-clamp-1 keep-all overflow-hidden Caption-12Md md:Body2-14Md text-gray-300">
                  {tutor.major}
                </p>
              )}
            </div>
          </div>
          {showBottomSheet && (
            <DSBottomSheet
              id={`bottom-sheet-tutor-${tutor.id}`}
              isOpen={showBottomSheet}
              setIsOpen={setShowBottomSheet}
            >
              <div className="scrollbar-new">
                <TutorBody id={tutor.id} noReserve setShowBottomSheet={setShowBottomSheet} />
              </div>
            </DSBottomSheet>
          )}
        </div>
      </>
    )
  }
}

const RookieTutorBadge = () => {
  const { t } = useTranslation()
  return <div className="text-[12px] font-bold text-red-500">{t(s_review.label_pointback)}</div>
}

export default TutorAvatar
